import * as React from "react";

import { isNullable, Nullable } from "@core/utils";

import { IPageFlowStep } from "../Interfaces/page-flow";

export interface IPageFlowContext extends IPageFlowActions {
  currentFlowStep: IPageFlowStep;
  flowStepHistory: IPageFlowStep[];
  currentFlowStepRef: React.MutableRefObject<IPageFlowStep>;
  flowStepHistoryRef: React.MutableRefObject<IPageFlowStep[]>;
  setCurrentFlowStep: React.Dispatch<React.SetStateAction<IPageFlowStep>>;
  setFlowStepHistory: React.Dispatch<React.SetStateAction<IPageFlowStep[]>>;
}

export const PageFlowContext = React.createContext<IPageFlowContext | null>(null);

export const usePageFlowContext = () => {
  const context = React.useContext(PageFlowContext);

  if (isNullable(context)) {
    throw new Error("usePageFlowContext must be used within a PageFlowContextProvider");
  }

  return context;
};

export const PageFlowContextProvider = ({ value, children }: React.ProviderProps<IPageFlowContext>) => {
  return <PageFlowContext.Provider value={value}>{children}</PageFlowContext.Provider>;
};

export interface ISubmitOptions {
  shouldStoreValues?: boolean;
}

export interface IPageFlowActions {
  handleSubmit: <T extends Record<string, any>>(
    options?: ISubmitOptions
  ) => (values: T) => Promise<Nullable<boolean>>;
  handlePrevious: () => void;
  resetForm: () => void;
}

export const createPageFlowContext = <T extends IPageFlowStep>(initialStep: T) => {
  const [currentFlowStep, setCurrentFlowStep] = React.useState<T>(initialStep);
  const [flowStepHistory, setFlowStepHistory] = React.useState<T[]>([initialStep]);

  const currentFlowStepRef = React.useRef<T>(initialStep);
  const flowStepHistoryRef = React.useRef<T[]>([initialStep]);

  React.useEffect(() => {
    currentFlowStepRef.current = currentFlowStep;
  }, [currentFlowStep]);

  React.useEffect(() => {
    flowStepHistoryRef.current = flowStepHistory;
  }, [flowStepHistory]);

  return {
    currentFlowStep,
    flowStepHistory,
    currentFlowStepRef,
    flowStepHistoryRef,
    setCurrentFlowStep,
    setFlowStepHistory
  };
};
